import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { Building2, Link2 } from 'lucide-react';
import Field from '../ui/Field';
import EntityPicker from '../ui/EntityPicker';
import { pickableMilestones } from '../projects/taskFormModel';
import {
  EMPTY_CHAIN, chainHasMilestone, engagementsOfType, setChainField, typesWithOpenEngagement, selectedInternalProject,
  type ChainEngagement, type ChainField, type ChainValue, type InternalProjectChoice,
} from './engagementChainModel';
import { useInternalOrgId } from '../../hooks/useInternalOrg';
import { usePermissions } from '../../hooks/usePermissions';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstPickerOptions } from '../../utils/internalWorkOption';

/**
 * P-02b (D-26, plan §6.6) — the task FILTER of the timer and of "רישום שעות":
 * customer → type → engagement → (milestone, on a project / development) →
 * the task list. Every level is optional (a filter, not a placement); a
 * change clears the levels below it. Customers: active only (D-30, as before).
 * `ticketParams` is what the task list request sends.
 */
export function useEngagementFilter(enabled = true) {
  const [picked, setValue] = useState<ChainValue>(EMPTY_CHAIN);
  // D-275 — an internal-only employee: always "עבודה פנימית OTE", no customer choice.
  const { internalOnly, internalWorkEngagementId } = usePermissions();
  const internalOrgId = useInternalOrgId();
  const value: ChainValue = internalOnly && internalOrgId && picked.organizationId !== internalOrgId
    ? { ...EMPTY_CHAIN, organizationId: internalOrgId } : picked;
  // D-274 §3 / D-275 — on "עבודה פנימית OTE": "פרויקט (לא חובה)" instead of type / engagement.
  const companyMode = !!internalOrgId && value.organizationId === internalOrgId;

  const { data: orgsData } = useQuery({
    queryKey: ['organizations', 'active-pickers', 'with-internal'],
    // D-274 §1 / D-275 — "עבודה פנימית OTE" is a choice for every OTE employee.
    queryFn: () => api.getOrganizations({ status: 'active', include_internal: 1 }),
    staleTime: 300_000,
    enabled: enabled && !internalOnly,
  });
  const organizations = (orgsData?.data ?? []) as { id: string; name: string }[];

  const { data: engRes } = useQuery({
    queryKey: ['engagements', 'chain', value.organizationId],
    queryFn: () => api.getEngagements({ organization_id: value.organizationId, limit: 200 }),
    staleTime: 30_000,
    enabled: enabled && !!value.organizationId && !companyMode,
  });
  const { data: ipRes } = useQuery({
    queryKey: ['engagements', 'internal-projects'],
    queryFn: () => api.getInternalProjects(),
    staleTime: 60_000,
    enabled: enabled && companyMode,
  });
  const { data: iwRes } = useQuery({
    queryKey: ['engagements', 'internal-work'],
    queryFn: () => api.getInternalWork(),
    staleTime: 5 * 60_000,
    enabled: enabled && companyMode && !internalOnly,
  });
  const internalWorkId = internalOnly ? (internalWorkEngagementId ?? '') : (iwRes?.data?.engagement_id ?? '');
  const internalProjects = (ipRes?.data ?? []) as InternalProjectChoice[];
  const internalProject = companyMode ? selectedInternalProject(value, internalProjects) : null;
  const engagements = (engRes?.data ?? []) as unknown as ChainEngagement[];
  const types = typesWithOpenEngagement(engagements);
  const engagementOptions = value.engagementType ? engagementsOfType(engagements, value.engagementType, value.engagementId) : [];
  const selected = engagements.find((e) => e.id === value.engagementId) ?? null;
  const showMilestone = companyMode
    ? !!internalProject && internalProject.milestones.length > 0
    : !!selected && chainHasMilestone(selected.type) && !!selected.project_id;

  const { data: msRes } = useQuery({
    queryKey: ['milestones', selected?.project_id ?? ''],
    queryFn: () => api.getMilestones(selected!.project_id!),
    staleTime: 60_000,
    enabled: enabled && showMilestone && !companyMode,
  });
  const milestones: Array<{ id: string; name: string }> = companyMode
    ? [...(internalProject?.milestones ?? [])]
    : pickableMilestones(msRes?.data ?? [], value.milestoneId);

  // D-275: an internal-only employee's levels sit under "עבודה פנימית OTE" (her fixed customer).
  const set = (field: ChainField, next: string) => setValue((v) => setChainField(
    internalOnly && internalOrgId && v.organizationId !== internalOrgId ? { ...EMPTY_CHAIN, organizationId: internalOrgId } : v, field, next,
  ));

  return {
    value, set, organizations, types, engagementOptions, selected, showMilestone, milestones,
    /** D-275 — no customer field (always "עבודה פנימית OTE"). */
    internalOnly,
    /** D-274 §3 / D-275 — "עבודה פנימית OTE": the project filter ('' = all of it). */
    companyMode, internalProjects, internalProject, internalWorkId,
    /** `GET /tickets` filter params of the chosen levels. */
    ticketParams: {
      organization_id: value.organizationId || undefined,
      engagement_type: !companyMode && !value.engagementId && value.engagementType ? value.engagementType : undefined,
      engagement_id: value.engagementId || undefined,
      milestone_id: value.milestoneId || undefined,
    },
  };
}

export type EngagementFilter = ReturnType<typeof useEngagementFilter>;

/**
 * The four filter selects. `compact`: the timer dialog's small labels; else
 * `ui/Field` (the work-log dialog). `allowAllCustomers`: the "all customers"
 * option (admin), as the old customer filter had.
 */
export default function EngagementFilterFields({ filter, idPrefix, allowAllCustomers, compact = false }: {
  filter: EngagementFilter;
  idPrefix: string;
  allowAllCustomers: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { value, set } = filter;
  const internalOrgId = useInternalOrgId();

  const field = (id: string, label: string, control: React.ReactElement) => (compact ? (
    <div key={id}>
      <label htmlFor={id} className="text-xs text-surface-500 mb-1 block">{label}</label>
      {control}
    </div>
  ) : (
    <Field key={id} id={id} label={label}>{control}</Field>
  ));
  const selectClass = compact ? 'input text-sm w-full' : 'input';

  // D-274 §3 / D-275 — on "עבודה פנימית OTE": one "פרויקט (לא חובה)" filter (all /
  // "ישירות תחת OTE" / a project), then "אבן דרך (לא חובה)" for a project that has them.
  const companyFields = filter.companyMode ? (
    <>
      {field(`${idPrefix}-project`, t('engagementChain.internalProject'), (
        <EntityPicker
          id={`${idPrefix}-project`}
          icon={Link2}
          emptyLabel={t('engagementChain.allInternalWork')}
          options={[
            ...(filter.internalWorkId ? [{ value: filter.internalWorkId, label: t('engagementChain.directlyUnderOte') }] : []),
            ...filter.internalProjects.map((p) => ({ value: p.engagement_id, label: p.name })),
          ]}
          value={value.engagementId}
          onChange={(v) => set('engagementId', v)}
        />
      ))}
      {filter.showMilestone && field(`${idPrefix}-milestone`, t('engagementChain.internalMilestone'), (
        <EntityPicker
          id={`${idPrefix}-milestone`}
          emptyLabel={t('engagementChain.allMilestones')}
          options={filter.milestones.map((m) => ({ value: m.id, label: m.name }))}
          value={value.milestoneId}
          onChange={(v) => set('milestoneId', v)}
        />
      ))}
    </>
  ) : null;

  return (
    <>
      {!filter.internalOnly && field(`${idPrefix}-org`, t('timer.filterByOrg'), (
        <EntityPicker
          id={`${idPrefix}-org`}
          icon={Building2}
          placeholder={t('engagementChain.selectCustomer')}
          emptyLabel={allowAllCustomers ? t('timer.allOrganizations') : undefined}
          options={internalFirstPickerOptions(filter.organizations, internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY))}
          value={value.organizationId}
          onChange={(v) => set('organizationId', v)}
        />
      ))}
      {companyFields}
      {!filter.companyMode && field(`${idPrefix}-type`, t('engagementChain.type'), (
        <select id={`${idPrefix}-type`} value={value.engagementType} onChange={(e) => set('engagementType', e.target.value)} className={selectClass} disabled={!value.organizationId}>
          <option value="">{t('engagementChain.allTypes')}</option>
          {filter.types.map((ty) => <option key={ty} value={ty}>{t(`engagementDetail.types.${ty}`)}</option>)}
        </select>
      ))}
      {!filter.companyMode && field(`${idPrefix}-engagement`, t('engagementChain.engagement'), (
        <EntityPicker
          id={`${idPrefix}-engagement`}
          icon={Link2}
          emptyLabel={t('engagementChain.allEngagements')}
          options={filter.engagementOptions.map((e) => ({ value: e.id, label: e.child_name || t('engagementChain.unnamed') }))}
          value={value.engagementId}
          onChange={(v) => set('engagementId', v)}
          disabled={!value.engagementType}
        />
      ))}
      {!filter.companyMode && filter.showMilestone && field(`${idPrefix}-milestone`, t('taskForm.milestone'), (
        <EntityPicker
          id={`${idPrefix}-milestone`}
          emptyLabel={t('engagementChain.allMilestones')}
          options={filter.milestones.map((m) => ({ value: m.id, label: m.name }))}
          value={value.milestoneId}
          onChange={(v) => set('milestoneId', v)}
        />
      ))}
    </>
  );
}
